// Agile Software Design System components (Button, IconButton, Badge, Eyebrow,
// Field, Input, Select, Switch, Tabs, Toast, Dialog), rebuilt to the DS spec
// from the design hand-off. Styling lives in styles.css under .ds-*.

import {
  useEffect,
  useId,
  useRef,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { Icon } from "./Icon";

type BtnVariant = "primary" | "accent" | "secondary" | "ghost" | "inverse";
type Size = "sm" | "md" | "lg";

export function Button({
  children,
  variant = "primary",
  size = "md",
  icon,
  arrow,
  fullWidth,
  disabled,
  busy,
  type = "button",
  onClick,
  style,
}: {
  children: ReactNode;
  variant?: BtnVariant;
  size?: Size;
  icon?: string;
  arrow?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  busy?: boolean;
  type?: "button" | "submit";
  onClick?: () => void;
  style?: CSSProperties;
}) {
  return (
    <button
      type={type}
      className={`ds-btn ds-btn-${variant} ds-btn-${size}${fullWidth ? " ds-btn-full" : ""}`}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      onClick={onClick}
      style={style}
    >
      {icon ? <Icon name={icon} size={size === "sm" ? 15 : 16} /> : null}
      <span>{children}</span>
      {arrow ? <span aria-hidden>→</span> : null}
    </button>
  );
}

export function IconButton({
  icon,
  label,
  variant = "secondary",
  size = "md",
  onClick,
  disabled,
}: {
  icon: string;
  label: string;
  variant?: "primary" | "secondary" | "ghost" | "inverse";
  size?: Size;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={`ds-iconbtn ds-iconbtn-${variant} ds-iconbtn-${size}`}
      onClick={onClick}
      disabled={disabled}
    >
      <Icon name={icon} size={size === "sm" ? 16 : 18} />
    </button>
  );
}

export type Tone = "navy" | "teal" | "neutral" | "success" | "warning" | "danger";

export function Badge({ children, tone = "neutral", dot }: { children: ReactNode; tone?: Tone; dot?: boolean }) {
  return (
    <span className={`ds-badge ds-badge-${tone}`}>
      {dot ? <span className="ds-badge-dot" /> : null}
      {children}
    </span>
  );
}

export function Eyebrow({
  children,
  size = "md",
  tone = "teal",
}: {
  children: ReactNode;
  size?: "sm" | "md";
  tone?: "teal" | "navy" | "muted";
}) {
  return <span className={`ds-eyebrow ds-eyebrow-${size} ds-eyebrow-${tone}`}>{children}</span>;
}

export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <div className="ds-field">
      {label ? (
        <label className="ds-field-label" htmlFor={htmlFor}>
          {label}
        </label>
      ) : null}
      {children}
      {error ? (
        <span className="ds-field-error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="ds-field-hint">{hint}</span>
      ) : null}
    </div>
  );
}

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "onChange"> & {
  icon?: string;
  size?: Size;
  invalid?: boolean;
  mono?: boolean;
  multiline?: boolean;
  rows?: number;
  onChange?: (v: string) => void;
};

export function Input({ icon, size = "md", invalid, mono, multiline, rows = 4, onChange, className, ...rest }: InputProps) {
  const cls = `ds-input ds-input-${size}${invalid ? " is-invalid" : ""}${multiline ? " is-multi" : ""}${mono ? " is-mono" : ""}${className ? ` ${className}` : ""}`;
  return (
    <div className={cls}>
      {icon && !multiline ? <Icon name={icon} size={16} style={{ color: "var(--text-subtle)" }} /> : null}
      {multiline ? (
        <textarea
          id={rest.id}
          rows={rows}
          value={rest.value as string}
          placeholder={rest.placeholder}
          spellCheck={false}
          autoComplete="off"
          onChange={(e) => onChange?.(e.target.value)}
        />
      ) : (
        <input {...rest} onChange={(e) => onChange?.(e.target.value)} />
      )}
    </div>
  );
}

export function Select<T extends string>({
  options,
  value,
  onChange,
  size = "md",
  id,
  disabled,
  label,
}: {
  options: (T | { value: T; label: string })[];
  value: T;
  onChange: (v: T) => void;
  size?: Size;
  id?: string;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <div className={`ds-select ds-input-${size}`}>
      <select id={id} value={value} disabled={disabled} aria-label={label} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => {
          const opt = typeof o === "string" ? { value: o, label: o } : o;
          return (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          );
        })}
      </select>
      <Icon name="chevron-down" size={16} style={{ position: "absolute", right: 12, color: "var(--text-muted)", pointerEvents: "none" }} />
    </div>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
  ariaLabel,
}: {
  checked: boolean;
  onChange?: (v: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  return (
    <label className={`ds-switch${disabled ? " is-disabled" : ""}`}>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => onChange?.(e.target.checked)}
      />
      <span className="ds-switch-track" aria-hidden>
        <span className="ds-switch-knob" />
      </span>
      {label ? <span className="ds-switch-label">{label}</span> : null}
    </label>
  );
}

export function Tabs<T extends string>({
  items,
  value,
  onChange,
  variant = "underline",
}: {
  items: (T | { value: T; label: string; count?: number | string })[];
  value: T;
  onChange: (v: T) => void;
  variant?: "underline" | "pill";
}) {
  return (
    <div role="tablist" className={`ds-tabs ds-tabs-${variant}`}>
      {items.map((it) => {
        const item = typeof it === "string" ? { value: it, label: it as string, count: undefined } : it;
        const on = item.value === value;
        return (
          <button key={item.value} type="button" role="tab" aria-selected={on} className={on ? "is-on" : ""} onClick={() => onChange(item.value)}>
            {item.label}
            {item.count !== undefined && item.count !== "" ? <span className="ds-tabs-count">{item.count}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

const TOAST_ICON = { success: "check-circle", info: "info", warning: "alert-triangle", danger: "alert-circle" } as const;
export type ToastTone = keyof typeof TOAST_ICON;

export function Toast({
  title,
  message,
  tone = "success",
  onClose,
}: {
  title: string;
  message?: string;
  tone?: ToastTone;
  onClose: () => void;
}) {
  return (
    <div className="ds-toast" role="status">
      <span className={`ds-toast-icon is-${tone}`}>
        <Icon name={TOAST_ICON[tone]} size={18} />
      </span>
      <div className="ds-toast-body">
        <span className="ds-toast-title">{title}</span>
        {message ? <span className="ds-toast-msg">{message}</span> : null}
      </div>
      <button type="button" className="ds-toast-close" aria-label="Dismiss" onClick={onClose}>
        <Icon name="x" size={16} />
      </button>
    </div>
  );
}

/** Modal dialog with the DS accent rule, title/subtitle, and an optional footer. */
export function Dialog({
  title,
  subtitle,
  onClose,
  width = 560,
  footer,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  width?: number;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    // Focus the first field (or the panel) so keyboard users land inside.
    const first = panel.current?.querySelector<HTMLElement>("input, textarea, select");
    (first ?? panel.current)?.focus();
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="ds-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={panel} className="ds-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} style={{ maxWidth: width }}>
        <div className="accent-rule" style={{ width: 77 }} />
        <div className="ds-dialog-head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ds-dialog-title" id={titleId}>
              {title}
            </div>
            {subtitle ? <div className="ds-dialog-sub">{subtitle}</div> : null}
          </div>
          <IconButton icon="x" label="Close" variant="ghost" size="sm" onClick={onClose} />
        </div>
        {children}
        {footer ? <div className="ds-dialog-foot">{footer}</div> : null}
      </div>
    </div>
  );
}

/** Small selectable pill, used for request types/fields. */
export function Chip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button type="button" className={`chip-pill${on ? " is-on" : ""}`} aria-pressed={on} onClick={onClick}>
      {label}
    </button>
  );
}

/** 36/40 px tinted icon tile. */
export function IconTile({ icon, size = 36 }: { icon: string; size?: number }) {
  return (
    <div className="icon-tile" style={{ width: size, height: size }}>
      <Icon name={icon} size={size >= 40 ? 20 : 18} />
    </div>
  );
}

export function Avatar({ initials, size = 32 }: { initials: string; size?: number }) {
  return (
    <div className="avatar" style={{ width: size, height: size }}>
      {initials}
    </div>
  );
}
